/**
 * staff-hover-tooltip.js
 * -------------------------------
 * Alt+hover a contact-trigger button to reveal which staff member's inbox it
 * routes to, and the form it belongs to. Not tied to any visible UI —
 * intentionally undiscoverable without the key combo.
 *
 * Data source: window.__staffTooltipMap (pageType -> staff label) and
 * window.__defaultModalType (current page's default pageType), both embedded
 * by views/layout.ejs from res.locals set in middleware/modalConfigMiddleware.js.
 *
 * Hit-testing is done fresh from the current cursor position every time (via
 * elementFromPoint, falling back to a padded bounding-box scan) rather than
 * tracked across mousemove events — several .btn variants apply a hover
 * transform (e.g. translateY(-2px) in _button.scss), which shifts the
 * element just enough to make live target-tracking across events flicker in
 * and out. Recomputing on demand avoids carrying that stale/jittery state.
 */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var staffMap = window.__staffTooltipMap || {};
  var defaultPageType = window.__defaultModalType || 'default';
  if (!Object.keys(staffMap).length) return;

  var TRIGGER_SELECTOR = '.contact-us-btn, [data-contact-trigger]';
  // Slop around each button's rect, in px — bigger than any .btn hover-transform
  // shift, so a hover-lift effect can't push the cursor outside the hit area.
  var HIT_TEST_PADDING = 8;

  var altActive = false;
  var lastX = 0;
  var lastY = 0;
  var tooltipEl = null;

  function pageTypeForButton(button) {
    return button.dataset.modalType || defaultPageType;
  }

  function findTriggerAtPoint(x, y) {
    var el = document.elementFromPoint(x, y);
    var direct = el && el.closest ? el.closest(TRIGGER_SELECTOR) : null;
    if (direct) return direct;

    // Fallback: padded bounding-box test. Covers the moment a CSS hover
    // transform has shifted the button just past the live elementFromPoint hit.
    var candidates = document.querySelectorAll(TRIGGER_SELECTOR);
    for (var i = 0; i < candidates.length; i++) {
      var r = candidates[i].getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue;
      if (
        x >= r.left - HIT_TEST_PADDING && x <= r.right + HIT_TEST_PADDING &&
        y >= r.top - HIT_TEST_PADDING && y <= r.bottom + HIT_TEST_PADDING
      ) {
        return candidates[i];
      }
    }
    return null;
  }

  function ensureTooltip() {
    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.className = 'staff-hover-tooltip';
      tooltipEl.setAttribute('role', 'tooltip');
      document.body.appendChild(tooltipEl);
    }
    return tooltipEl;
  }

  function hideTooltip() {
    if (tooltipEl) tooltipEl.classList.remove('is-visible');
  }

  function updateTooltip() {
    if (!altActive) return;
    var button = findTriggerAtPoint(lastX, lastY);
    if (!button) {
      hideTooltip();
      return;
    }
    var pageType = pageTypeForButton(button);
    var staffLabel = staffMap[pageType];
    if (!staffLabel) {
      hideTooltip();
      return;
    }
    var tip = ensureTooltip();
    tip.textContent = staffLabel + ' — ' + pageType;
    tip.style.left = (lastX + 14) + 'px';
    tip.style.top = (lastY + 14) + 'px';
    tip.classList.add('is-visible');
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Alt') return;
    // Without this, a bare Alt press-and-release is treated by the browser as
    // the "focus the menu bar / show access-key hints" accelerator (Windows
    // Chrome/Firefox). After the first cycle, keyboard focus shifts off the
    // page entirely, so every subsequent Alt keydown silently stops reaching
    // this listener until the user clicks back into the page.
    e.preventDefault();
    if (altActive) return;
    altActive = true;
    updateTooltip();
  });

  document.addEventListener('keyup', function (e) {
    if (e.key !== 'Alt') return;
    e.preventDefault();
    altActive = false;
    hideTooltip();
  });

  window.addEventListener('blur', function () {
    altActive = false;
    hideTooltip();
  });

  document.addEventListener('mousemove', function (e) {
    lastX = e.clientX;
    lastY = e.clientY;
    if (altActive) updateTooltip();
  });
})();
